import { useCallback } from 'react';

import type { PublicRunSnapshot } from '../../../contracts/harness.types';
import {
  CHILD_RUN_KIND,
  type ChildRunDiscovery,
} from '../../harness/child-runs/discovery';
import { useChildRunDiscoveries } from '../../harness/child-runs/use-child-run-discoveries';
import type { RunStreamEventObserver } from '../../query/useRunStreamLifecycle';

export interface ProjectRunStreamObserver {
  /**
   * Computer-use sub-agents discovered on this run's per-run stream +
   * snapshot, awaiting executor attach. Per-run-local (no global store).
   */
  discoveries: ChildRunDiscovery[];
  /**
   * Recording sub-agents discovered on this run's per-run stream +
   * snapshot, awaiting the dedicated recording executor attach.
   * Per-run-local (no global store). Only `ProjectsBackgroundRunner`
   * consumes these for attach — the chat transport does not.
   */
  recordingDiscoveries: ChildRunDiscovery[];
  /**
   * File (`file_tools`) sub-agents discovered on this run's per-run stream +
   * snapshot, awaiting the dedicated headless file executor attach.
   * Per-run-local (no global store). Only `ProjectsBackgroundRunner`
   * consumes these for attach — the chat transport does not.
   */
  fileDiscoveries: ChildRunDiscovery[];
  /**
   * Single observer to wire into the parent run's `onStreamEvent`. Fans the
   * per-run LLM stream out to every project-side consumer that rides it:
   * computer-use child discovery (`tool_execution_start` metadata),
   * recording child discovery (same metadata, different kind), file child
   * discovery, and then the owner's `onForeignStreamEvent` (for the chat:
   * Evidence sub-agent chunk ingestion and Slide Builder patches).
   */
  onStreamEvent: RunStreamEventObserver;
  /** Drop a computer-use discovery once its child run reaches a terminal stream event. */
  removeDiscovery: (childRunId: string) => void;
  /** Drop a recording discovery once its child run reaches a terminal stream event. */
  removeRecordingDiscovery: (childRunId: string) => void;
  /** Drop a file discovery once its child run reaches a terminal stream event. */
  removeFileDiscovery: (childRunId: string) => void;
  /**
   * Reconcile computer-use, recording, and file discoveries from a run
   * snapshot's `child_runs`. No-ops while the snapshot is undefined.
   * Drive it from a `[run]` effect.
   */
  reconcileFromSnapshot: (run: PublicRunSnapshot | null | undefined) => void;
}

export interface ProjectRunStreamObserverOptions {
  /**
   * The run's stream consumers outside this domain, handed every event after the
   * discoveries: Evidence's `evidence.chunk` ingest into `evidenceSessionsAtom`
   * and Slide Builder's patches to the open deck. Exactly one owner per run may
   * pass one: the chat transport and the background runner each open their OWN
   * unordered SSE connection to the same parent run, and the Evidence reducer
   * appends and can't fold out-of-order indices — so two ingesters would
   * duplicate or corrupt the timeline, and a `slide_update` applied from two
   * connections would double-insert an added slide. The chat transport is the
   * canonical owner because evidence only renders there; the background runner
   * and the overlay pass none. Pass a module-level function: the observer's
   * identity follows it.
   */
  onForeignStreamEvent?: RunStreamEventObserver;
}

/**
 * Composes the per-run consumers that read a project parent run's LLM
 * stream into one `onStreamEvent` observer, shared by `useProjectTransport`
 * (chat surface) and the background runner so neither duplicates the wiring.
 *
 * Evidence chunks ride the parent run's stream because the Evidence
 * sub-agent is a headless tool loop inside the parent run — there is no
 * separate child run to subscribe to (unlike computer-use and recording,
 * which open child streams from their discoveries). Computer-use and
 * recording discovery are per-owner local state (idempotent across both
 * owners), but evidence folds into a shared atom, so it is gated to a
 * single owner: the one that passes `onForeignStreamEvent`.
 */
export const useProjectRunStreamObserver = (
  agentRunId: string | undefined,
  { onForeignStreamEvent }: ProjectRunStreamObserverOptions = {}
): ProjectRunStreamObserver => {
  const {
    discoveries,
    onParentStreamEvent: onComputerUseStreamEvent,
    reconcileFromSnapshot: reconcileComputerUse,
    removeDiscovery,
  } = useChildRunDiscoveries(CHILD_RUN_KIND.computerUse, agentRunId);
  const {
    discoveries: recordingDiscoveries,
    onParentStreamEvent: onRecordingStreamEvent,
    reconcileFromSnapshot: reconcileRecording,
    removeDiscovery: removeRecordingDiscovery,
  } = useChildRunDiscoveries(CHILD_RUN_KIND.recording, agentRunId);
  const {
    discoveries: fileDiscoveries,
    onParentStreamEvent: onFileStreamEvent,
    reconcileFromSnapshot: reconcileFile,
    removeDiscovery: removeFileDiscovery,
  } = useChildRunDiscoveries(CHILD_RUN_KIND.fileTools, agentRunId);

  const onStreamEvent = useCallback<RunStreamEventObserver>(
    (id, event) => {
      onComputerUseStreamEvent(id, event);
      onRecordingStreamEvent(id, event);
      onFileStreamEvent(id, event);
      onForeignStreamEvent?.(id, event);
    },
    [
      onComputerUseStreamEvent,
      onRecordingStreamEvent,
      onFileStreamEvent,
      onForeignStreamEvent,
    ]
  );

  const reconcileFromSnapshot = useCallback(
    (run: PublicRunSnapshot | null | undefined) => {
      reconcileComputerUse(run);
      reconcileRecording(run);
      reconcileFile(run);
    },
    [reconcileComputerUse, reconcileRecording, reconcileFile]
  );

  return {
    discoveries,
    recordingDiscoveries,
    fileDiscoveries,
    onStreamEvent,
    removeDiscovery,
    removeRecordingDiscovery,
    removeFileDiscovery,
    reconcileFromSnapshot,
  };
};
